import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ISearchRecord } from '../../../types/search';
import { IFacilityDischargeData, IOutfall } from '../../../types/discharge';
import { auth } from '../../../services/firebase';
import { fetchEffluentChartData } from '../../../utils/echoDischargeApi';
import { fetchAttainsWatershed } from '../../../utils/attainsApi';
import { applyPlotLatLong, isValidLatLon } from '../../../util/favoriteApi.util';
import { resolveRecordLocation } from '../../../util/entityLocation.util';

const NO_COORDS_ERROR = 'This facility has no location coordinates on record.';

// ─── public types ────────────────────────────────────────────────────────────

export type Mode = 'air' | 'water' | 'land';

export type ApiState =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | { state: 'ok'; data: any };

export interface IFacilityModeData {
  activeMarkerRecord: ISearchRecord | null;

  currentMode: Mode;
  setCurrentMode: (m: Mode) => void;

  registryId: string | null;
  zipCode: string | null;

  airnow: ApiState;
  nws: ApiState;
  echo: ApiState;
  usgs: ApiState;
  nwps: ApiState;
  eq: ApiState;
  attains: ApiState;

  dischargeData: IFacilityDischargeData[] | null;
  dischargeLoading: boolean;
  dischargeError: string | null;
  dischargeFacilityLocation: { lat: number; lon: number } | null;
  dischargeOutfalls: { lat: number; lon: number }[];

  selectedOutfall: IOutfall | null;
  setSelectedOutfall: (o: IOutfall | null) => void;

  // Right-side detail panels are mutually exclusive: only one may be open.
  attainsPanelOpen: boolean;
  openAttainsDetail: () => void;
  closeAttainsDetail: () => void;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const TIMEOUT_MS = 60000;

function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function degToCompass(deg: number | null | undefined): string {
  if (deg === null || deg === undefined) return '';
  const dirs = [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW',
  ];
  return dirs[Math.round((deg % 360) / 22.5) % 16];
}

async function fetchWithTimeout(url: string, options?: RequestInit): Promise<any> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: ctrl.signal, ...options });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  } catch (e: any) {
    if (e.name === 'AbortError') throw new Error('Timed out');
    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function apiFetchAirNow(lat: number, lon: number): Promise<any> {
  const key = process.env.REACT_APP_AIRNOW_API_KEY;
  if (!key) throw new Error('AirNow API key not configured (REACT_APP_AIRNOW_API_KEY)');
  return fetchWithTimeout(
    `https://www.airnowapi.org/aq/observation/latLong/current/` +
      `?format=application/json&latitude=${lat}&longitude=${lon}&distance=25&API_KEY=${key}`
  );
}

async function apiFetchNWS(lat: number, lon: number): Promise<any> {
  const geo = { headers: { Accept: 'application/geo+json' } };
  const pts = await fetchWithTimeout(`https://api.weather.gov/points/${lat},${lon}`, geo);
  const stationsUrl = pts?.properties?.observationStations;
  if (!stationsUrl) throw new Error('NWS points missing observationStations URL');
  const stations = await fetchWithTimeout(stationsUrl, geo);
  const first = stations?.features?.[0];
  if (!first) throw new Error('No NWS stations near point');
  const stationId: string = first.properties.stationIdentifier;
  const stationName: string = first.properties.name;
  const sLat: number = first.geometry.coordinates[1];
  const sLon: number = first.geometry.coordinates[0];
  const obs = await fetchWithTimeout(
    `https://api.weather.gov/stations/${stationId}/observations/latest`,
    geo
  );
  const props = obs?.properties;
  const windSpeedKmh: number | null = props?.windSpeed?.value;
  const windSpeedMph = windSpeedKmh == null ? null : Math.round(windSpeedKmh * 0.621371);
  const windDirDeg: number | null = props?.windDirection?.value;
  return {
    windSpeedMph,
    windDirDeg,
    windDirCompass: degToCompass(windDirDeg),
    stationId,
    stationName,
    observedAt: props?.timestamp || null,
    distanceMiles: Math.round(haversineKm(lat, lon, sLat, sLon) * 0.621371 * 10) / 10,
  };
}

async function apiFetchECHO(zipCode: string, frsId: string): Promise<any> {
  const url = `${process.env.REACT_APP_CERES_URL}/api/echo/facility-info?zip=${zipCode}`;
  const data = await fetchWithTimeout(url);
  const facilities: any[] = data?.Results?.Facilities;
  if (!Array.isArray(facilities)) throw new Error('Unexpected ECHO payload');
  const match = facilities.find((f) => f?.RegistryID === frsId);
  if (!match) {
    return {
      available: false,
      reason: `FRS ${frsId} not found in ECHO for this location (${facilities.length} facilities scanned)`,
    };
  }
  return { available: true, facility: match };
}

async function apiFetchUSGSWater(lat: number, lon: number): Promise<any> {
  const BASE = 'https://api.waterdata.usgs.gov/ogcapi/v0/collections';
  const BBOX_HALF = 0.25;

  async function latestForParam(paramCode: string) {
    const minLon = (lon - BBOX_HALF).toFixed(4);
    const minLat = (lat - BBOX_HALF).toFixed(4);
    const maxLon = (lon + BBOX_HALF).toFixed(4);
    const maxLat = (lat + BBOX_HALF).toFixed(4);
    const data = await fetchWithTimeout(
      `${BASE}/latest-continuous/items?bbox=${minLon},${minLat},${maxLon},${maxLat}` +
        `&parameter_code=${paramCode}&f=json&limit=200`
    );
    const features: any[] = data?.features || [];
    if (features.length === 0) return null;
    features.forEach((f) => {
      const [fLon, fLat] = f.geometry?.coordinates || [];
      f._distKm = fLat != null ? haversineKm(lat, lon, fLat, fLon) : Infinity;
    });
    features.sort((a, b) => a._distKm - b._distKm);
    const nearest = features[0];
    const props = nearest.properties || {};
    return {
      monitoringLocationId: props.monitoring_location_id,
      value: props.value,
      time: props.time,
      distanceMiles: Math.round(nearest._distKm * 0.621371 * 10) / 10,
    };
  }

  async function locationName(id: string): Promise<string | null> {
    try {
      const data = await fetchWithTimeout(
        `${BASE}/monitoring-locations/items?monitoring_location_number=${encodeURIComponent(id)}&f=json&limit=1`
      );
      return data?.features?.[0]?.properties?.monitoring_location_name || null;
    } catch {
      return null;
    }
  }

  const flow = await latestForParam('00060');
  const stage = await latestForParam('00065');
  const chosen = flow || stage;
  if (!chosen) return { stationId: null };
  const stationId = chosen.monitoringLocationId?.replace(/^USGS-/, '') || null;
  const stationName = await locationName(stationId);
  return {
    stationId,
    stationName: stationName || chosen.monitoringLocationId,
    flowCfs: flow ? Number(flow.value) : null,
    gageHeightFt: stage ? Number(stage.value) : null,
    observedAt: chosen.time,
    distanceMiles: chosen.distanceMiles,
  };
}

async function apiFetchNWPS(lat: number, lon: number): Promise<any> {
  const BASE = 'https://api.water.noaa.gov/nwps/v1';
  const BBOX_HALF = 0.5;
  const minLon = (lon - BBOX_HALF).toFixed(4);
  const minLat = (lat - BBOX_HALF).toFixed(4);
  const maxLon = (lon + BBOX_HALF).toFixed(4);
  const maxLat = (lat + BBOX_HALF).toFixed(4);
  const data = await fetchWithTimeout(
    `${BASE}/gauges?srid=4326&bbox.xmin=${minLon}&bbox.ymin=${minLat}&bbox.xmax=${maxLon}&bbox.ymax=${maxLat}`
  );
  const gauges: any[] = data?.gauges || [];
  if (gauges.length === 0) {
    return {
      available: false,
      reason: `No NWPS gauge within ${Math.round(BBOX_HALF * 111)} km of facility`,
    };
  }
  gauges.forEach((gauge) => {
    gauge._distKm =
      gauge.latitude != null && gauge.longitude != null
        ? haversineKm(lat, lon, +gauge.latitude, +gauge.longitude)
        : Infinity;
  });
  gauges.sort((a, b) => a._distKm - b._distKm);
  const g = gauges[0];
  const forecast = await fetchWithTimeout(
    `${BASE}/gauges/${encodeURIComponent(g.lid)}/stageflow/forecast`
  );
  const forecastData: any[] = forecast?.data || forecast || [];
  const peak =
    Array.isArray(forecastData) && forecastData.length > 0
      ? forecastData.reduce(
          (acc: any, p: any) => (p.primary > (acc?.primary ?? -Infinity) ? p : acc),
          null
        )
      : null;
  return {
    lid: g.lid,
    gaugeName: g.name,
    currentCategory: forecast?.observed?.floodCategory || forecast?.status || null,
    forecastPeak: peak?.primary ?? null,
    forecastPeakCategory: peak?.floodCategory || null,
    forecastPeakTime: peak?.validTime || null,
  };
}

async function apiFetchEarthquake(lat: number, lon: number, radiusKm = 100): Promise<any> {
  const data = await fetchWithTimeout(
    'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_week.geojson'
  );
  const events: any[] = [];
  (data?.features || []).forEach((f: any) => {
    const mag: number = f.properties?.mag;
    if (typeof mag !== 'number' || mag < 2.5) return;
    const coords: number[] = f.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    const distKm = haversineKm(lat, lon, coords[1], coords[0]);
    if (distKm > radiusKm) return;
    events.push({
      mag,
      place: f.properties.place,
      distance_km: Math.round(distKm * 10) / 10,
    });
  });
  events.sort((a, b) => b.mag - a.mag);
  return { count: events.length, events };
}

// ─── context ─────────────────────────────────────────────────────────────────

const FacilityModeDataContext = createContext<IFacilityModeData | null>(null);

export const useFacilityModeData = (): IFacilityModeData => {
  const ctx = useContext(FacilityModeDataContext);
  if (!ctx) {
    throw new Error('useFacilityModeData must be used inside <FacilityModeDataProvider>');
  }
  return ctx;
};

interface IProviderProps {
  activeMarkerRecord: ISearchRecord | null;
  children: React.ReactNode;
}

export const FacilityModeDataProvider: React.FC<IProviderProps> = ({
  activeMarkerRecord,
  children,
}) => {
  const [currentMode, setCurrentMode] = useState<Mode>('air');
  const [registryId, setRegistryId] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string | null>(null);

  const [airnow, setAirnow] = useState<ApiState>({ state: 'loading' });
  const [nws, setNws] = useState<ApiState>({ state: 'loading' });
  const [echo, setEcho] = useState<ApiState>({ state: 'loading' });
  const [usgs, setUsgs] = useState<ApiState>({ state: 'loading' });
  const [nwps, setNwps] = useState<ApiState>({ state: 'loading' });
  const [eq, setEq] = useState<ApiState>({ state: 'loading' });
  const [attains, setAttains] = useState<ApiState>({ state: 'loading' });

  const [dischargeData, setDischargeData] = useState<IFacilityDischargeData[] | null>(null);
  const [dischargeLoading, setDischargeLoading] = useState<boolean>(false);
  const [dischargeError, setDischargeError] = useState<string | null>(null);
  const [selectedOutfall, setSelectedOutfall] = useState<IOutfall | null>(null);
  const [attainsPanelOpen, setAttainsPanelOpen] = useState<boolean>(false);

  // Enforce one-panel-at-a-time: selecting an outfall closes the ATTAINS detail panel.
  const selectOutfall = useCallback((o: IOutfall | null) => {
    if (o) setAttainsPanelOpen(false);
    setSelectedOutfall(o);
  }, []);
  const openAttainsDetail = useCallback(() => {
    setSelectedOutfall(null);
    setAttainsPanelOpen(true);
  }, []);
  const closeAttainsDetail = useCallback(() => setAttainsPanelOpen(false), []);

  // Close the ATTAINS detail panel whenever the selected facility changes.
  useEffect(() => {
    setAttainsPanelOpen(false);
  }, [activeMarkerRecord]);
  const [resolvedCoords, setResolvedCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [locationResolving, setLocationResolving] = useState(false);

  const entityNumber = activeMarkerRecord?.entityNumber ?? null;

  // Wait for query_ceres_ed when Firestore/ES record has no usable lat/lon yet.
  useEffect(() => {
    if (!activeMarkerRecord) {
      setResolvedCoords(null);
      setLocationResolving(false);
      return;
    }

    const local = applyPlotLatLong(activeMarkerRecord);
    if (isValidLatLon(local.location.lat, local.location.lon)) {
      setResolvedCoords({ lat: local.location.lat, lon: local.location.lon });
      setLocationResolving(false);
      return;
    }

    if (!activeMarkerRecord.entityNumber) {
      setResolvedCoords(null);
      setLocationResolving(false);
      return;
    }

    let cancelled = false;
    setLocationResolving(true);
    setResolvedCoords(null);

    resolveRecordLocation(activeMarkerRecord)
      .then((resolved) => {
        if (cancelled) return;
        if (isValidLatLon(resolved.location.lat, resolved.location.lon)) {
          setResolvedCoords({ lat: resolved.location.lat, lon: resolved.location.lon });
        } else {
          setResolvedCoords(null);
        }
      })
      .catch(() => {
        if (!cancelled) setResolvedCoords(null);
      })
      .finally(() => {
        if (!cancelled) setLocationResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeMarkerRecord]);

  const lat = resolvedCoords?.lat ?? null;
  const lon = resolvedCoords?.lon ?? null;
  const coordsReady = !locationResolving && isValidLatLon(lat ?? undefined, lon ?? undefined);

  // Registry / zip lookup ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!entityNumber) {
      setRegistryId(null);
      setZipCode(null);
      return;
    }
    let cancelled = false;
    auth.currentUser?.getIdToken().then((token) =>
      fetch(
        `${process.env.REACT_APP_CERES_URL}/api/registry-id?entityNumber=${encodeURIComponent(entityNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setRegistryId(data.registryId ?? null);
          setZipCode(data.zipCode ?? null);
        })
        .catch(() => {
          if (cancelled) return;
          setRegistryId(null);
          setZipCode(null);
        })
    );
    return () => {
      cancelled = true;
    };
  }, [entityNumber]);

  // ECHO ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!registryId || !zipCode) {
      setEcho({ state: 'loading' });
      return;
    }
    let cancelled = false;
    setEcho({ state: 'loading' });
    apiFetchECHO(zipCode, registryId)
      .then((d) => !cancelled && setEcho({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setEcho({ state: 'error', error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [zipCode, registryId]);

  // AirNow + NWS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeMarkerRecord || locationResolving) {
      setAirnow({ state: 'loading' });
      setNws({ state: 'loading' });
      return;
    }
    if (!coordsReady || lat == null || lon == null) {
      setAirnow({ state: 'error', error: NO_COORDS_ERROR });
      setNws({ state: 'error', error: NO_COORDS_ERROR });
      return;
    }
    let cancelled = false;
    setAirnow({ state: 'loading' });
    setNws({ state: 'loading' });
    apiFetchAirNow(lat, lon)
      .then((d) => !cancelled && setAirnow({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setAirnow({ state: 'error', error: e.message }));
    apiFetchNWS(lat, lon)
      .then((d) => !cancelled && setNws({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setNws({ state: 'error', error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [lat, lon, coordsReady, locationResolving, activeMarkerRecord]);

  // USGS Water + NWPS ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeMarkerRecord || locationResolving) {
      setUsgs({ state: 'loading' });
      setNwps({ state: 'loading' });
      return;
    }
    if (!coordsReady || lat == null || lon == null) {
      setUsgs({ state: 'error', error: NO_COORDS_ERROR });
      setNwps({ state: 'error', error: NO_COORDS_ERROR });
      return;
    }
    let cancelled = false;
    setUsgs({ state: 'loading' });
    setNwps({ state: 'loading' });
    apiFetchUSGSWater(lat, lon)
      .then((d) => !cancelled && setUsgs({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setUsgs({ state: 'error', error: e.message }));
    apiFetchNWPS(lat, lon)
      .then((d) => !cancelled && setNwps({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setNwps({ state: 'error', error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [lat, lon, coordsReady, locationResolving, activeMarkerRecord]);

  // Earthquake ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeMarkerRecord || locationResolving) {
      setEq({ state: 'loading' });
      return;
    }
    if (!coordsReady || lat == null || lon == null) {
      setEq({ state: 'error', error: NO_COORDS_ERROR });
      return;
    }
    let cancelled = false;
    setEq({ state: 'loading' });
    apiFetchEarthquake(lat, lon)
      .then((d) => !cancelled && setEq({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setEq({ state: 'error', error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [lat, lon, coordsReady, locationResolving, activeMarkerRecord]);

  // Discharge (DMR) — previously lived inside MyTundraMap ─────────────────────
  useEffect(() => {
    if (!activeMarkerRecord) {
      setDischargeData(null);
      setDischargeLoading(false);
      setDischargeError(null);
      setSelectedOutfall(null);
      return;
    }
    if (locationResolving) {
      setDischargeLoading(true);
      return;
    }
    let cancelled = false;
    const recordLat = coordsReady && lat != null ? lat : activeMarkerRecord.location.lat;
    const recordLon = coordsReady && lon != null ? lon : activeMarkerRecord.location.lon;
    const en = activeMarkerRecord.entityNumber;
    setDischargeData(null);
    setDischargeError(null);
    setDischargeLoading(true);
    setSelectedOutfall(null);
    Promise.resolve(auth.currentUser?.getIdToken())
      .then((token) =>
        fetch(`${process.env.REACT_APP_CERES_URL}/api/registry-id?entityNumber=${en}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
          .then((r) => r.json())
          .then((d) => d as { registryId: string | null; zipCode: string | null })
      )
      .then((data) => {
        if (cancelled) return undefined;
        if (!data.registryId) {
          setDischargeError(`No registry id found for ${en}.`);
          setDischargeLoading(false);
          return undefined;
        }
        return fetchEffluentChartData(data.registryId, recordLat, recordLon);
      })
      .then((data) => {
        if (cancelled || !data) return;
        setDischargeData(data);
        setDischargeLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setDischargeData(null);
        setDischargeError(e?.message || 'Failed to load discharge data.');
        setDischargeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeMarkerRecord, lat, lon, coordsReady, locationResolving]);

  // HUC-12 of the primary facility (from the ECHO CWA data already on each outfall).
  // The first facility may have no outfalls, so scan all facilities/outfalls and
  // take the first non-null HUC-12.
  const primaryHuc12 = useMemo<string | null>(() => {
    for (const f of dischargeData ?? []) {
      for (const o of f.outfalls ?? []) {
        if (o.huc12) return o.huc12;
      }
    }
    return null;
  }, [dischargeData]);

  // ATTAINS receiving-water status — keyed on the resolved HUC-12.
  useEffect(() => {
    if (!primaryHuc12) {
      setAttains({ state: 'loading' });
      return;
    }
    let cancelled = false;
    setAttains({ state: 'loading' });
    fetchAttainsWatershed(primaryHuc12)
      .then((d) => !cancelled && setAttains({ state: 'ok', data: d }))
      .catch((e) => !cancelled && setAttains({ state: 'error', error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [primaryHuc12]);

  const dischargeFacilityLocation = useMemo<{ lat: number; lon: number } | null>(() => {
    const first = dischargeData?.[0];
    if (first?.facilityLat != null && first?.facilityLon != null) {
      return { lat: first.facilityLat, lon: first.facilityLon };
    }
    return null;
  }, [dischargeData]);

  const dischargeOutfalls = useMemo<{ lat: number; lon: number }[]>(() => {
    if (!dischargeData) return [];
    return dischargeData.flatMap((f) =>
      f.outfalls.map((o) => ({ lat: o.markerLat, lon: o.markerLon }))
    );
  }, [dischargeData]);

  const value: IFacilityModeData = {
    activeMarkerRecord: activeMarkerRecord ?? null,
    currentMode,
    setCurrentMode,
    registryId,
    zipCode,
    airnow,
    nws,
    echo,
    usgs,
    nwps,
    eq,
    attains,
    dischargeData,
    dischargeLoading,
    dischargeError,
    dischargeFacilityLocation,
    dischargeOutfalls,
    selectedOutfall,
    setSelectedOutfall: selectOutfall,
    attainsPanelOpen,
    openAttainsDetail,
    closeAttainsDetail,
  };

  return (
    <FacilityModeDataContext.Provider value={value}>{children}</FacilityModeDataContext.Provider>
  );
};
